import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { UsersRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import API from '@/lib/api';
import { invalidateAllQueries } from '@/lib/queryCache';
import type { UserProps } from '@/lib/auth';
import { isSystemAdmin } from '@/lib/permissions';
import type { ImpersonationTargetDto, ReseedDemoDatabaseResponse } from '@paladin-monorepo/common';
import { toast } from 'sonner';

interface DemoModePanelProps {
  user: UserProps | null | undefined;
}

export function DemoModePanel({ user }: DemoModePanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<ImpersonationTargetDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ImpersonationTargetDto | null>(
    null,
  );
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );
  const [acting, setActing] = useState(false);
  const [reseedLoading, setReseedLoading] = useState(false);
  /** Bumps after reseed so `/auth/impersonation-targets` is refetched while the sheet stays open. */
  const [impersonationTargetsKey, setImpersonationTargetsKey] = useState(0);

  const visible = Boolean(
    user &&
      user.demoMode &&
      (isSystemAdmin(user) || Boolean(user.impersonatorId)),
  );
  const canUseImpersonation = Boolean(
    user && isSystemAdmin(user) && !user.impersonatorId,
  );
  const canReseedDemo = Boolean(user?.demoMode && canUseImpersonation);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    API.get<ImpersonationTargetDto[]>('/auth/impersonation-targets', {
      params: { q: '' },
    })
      .then((res) => {
        if (!cancelled) {
          setTargets(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTargets([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, impersonationTargetsKey]);

  const handleSheetOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTargets([]);
      setSelectedUser(null);
      setSelectedAccountId(null);
      setLoading(false);
    }
  };

  const handleImpersonate = async () => {
    if (!selectedUser || !selectedAccountId) {
      return;
    }
    setActing(true);
    try {
      await API.post('/auth/impersonate', {
        userId: selectedUser.userId,
        accountId: selectedAccountId,
      });
      await invalidateAllQueries(queryClient);
      // toast.success(t('demoMode.impersonateSuccess'));
      setOpen(false);
      setTargets([]);
      setSelectedUser(null);
      setSelectedAccountId(null);
      navigate('/');
    } catch {
      toast.error(t('demoMode.error'));
    } finally {
      setActing(false);
    }
  };

  const handleReseed = async () => {
    if (
      !window.confirm(
        t('demoMode.reseedConfirm'),
      )
    ) {
      return;
    }
    setReseedLoading(true);
    try {
      await API.post<ReseedDemoDatabaseResponse>('/auth/reseed-demo-database');
      setSelectedUser(null);
      setSelectedAccountId(null);
      setImpersonationTargetsKey((k) => k + 1);
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({
        queryKey: ['projects'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['/auth/impersonation-targets'],
      });
      toast.success(t('demoMode.reseedSuccess'));
    } catch {
      toast.error(t('demoMode.error'));
    } finally {
      setReseedLoading(false);
    }
  };

  const handleStop = async () => {
    setActing(true);
    try {
      await API.post('/auth/stop-impersonation');
      await invalidateAllQueries(queryClient);
      // toast.success(t('demoMode.stopSuccess'));
      setOpen(false);
      navigate('/admin');
    } catch {
      toast.error(t('demoMode.error'));
    } finally {
      setActing(false);
    }
  };

  if (!visible || !user) {
    return null;
  }

  return (
    <div className="fixed right-4 bottom-4 z-50 flex max-w-sm flex-col items-end gap-2">
      <Sheet modal={false} open={open} onOpenChange={handleSheetOpenChange}>
        <SheetTrigger asChild>
          <Button
            type="button"
            size="sm"
            className="shadow-lg"
            title={t('demoMode.fabTitle')}
          >
            <UsersRound className="mr-2 h-4 w-4" />
            {t('demoMode.fabTitle')}
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md px-4"
          onPointerDownOutside={(e) => {
            const t = e.target as HTMLElement;
            if (
              t.closest('[data-slot="combobox-content"]') ||
              t.closest('[data-slot="select-content"]')
            ) {
              e.preventDefault();
            }
          }}
        >
          <SheetHeader>
            <SheetTitle>{t('demoMode.panelTitle')}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-4">
            {user.impersonatorId ? (
              <div className="flex flex-col gap-3 rounded-md border border-brand-primary/30 bg-brand-primary/10 p-3 text-sm text-foreground shadow-sm">
                <div>
                  <p>{t('demoMode.impersonatingAs', { email: user.email })}</p>
                  {user.impersonatorEmail ? (
                    <p className="text-muted-foreground mt-1">
                      {t('demoMode.impersonator', {
                        email: user.impersonatorEmail,
                      })}
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="self-start"
                  disabled={acting}
                  onClick={handleStop}
                >
                  {t('demoMode.stop')}
                </Button>
              </div>
            ) : null}
            {canReseedDemo ? (
              <div className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <p className="text-sm">{t('demoMode.reseedHint')}</p>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={reseedLoading || acting}
                  onClick={handleReseed}
                >
                  {reseedLoading ? t('demoMode.reseedLoading') : t('demoMode.reseed')}
                </Button>
              </div>
            ) : null}
            {canUseImpersonation ? (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="demo-user-combobox">
                    {t('demoMode.searchLabel')}
                  </Label>
                  <Combobox<ImpersonationTargetDto>
                    items={targets}
                    itemToStringValue={(item) =>
                      `${item.label} (${item.email})`
                    }
                    isItemEqualToValue={(a, b) => a.userId === b.userId}
                    value={selectedUser}
                    onValueChange={(value) => {
                      const next = value as ImpersonationTargetDto | null;
                      setSelectedUser(next);
                      setSelectedAccountId(
                        next?.memberships?.length
                          ? next.memberships[0].accountId
                          : null,
                      );
                    }}
                  >
                    <ComboboxInput
                      id="demo-user-combobox"
                      placeholder={t('demoMode.searchPlaceholder')}
                      className="w-full"
                      autoComplete="off"
                      showClear={!!selectedUser}
                    />
                    <ComboboxContent>
                      <ComboboxEmpty>
                        {loading
                          ? t('demoMode.loading')
                          : t('demoMode.noResults')}
                      </ComboboxEmpty>
                      <ComboboxList>
                        {(item) => {
                          const row = item as ImpersonationTargetDto;
                          return (
                            <ComboboxItem
                              key={row.userId}
                              value={row}
                              className="flex flex-col items-start gap-0.5"
                            >
                              <span className="font-medium">{row.label}</span>
                              <span className="text-muted-foreground text-xs">
                                {row.email}
                              </span>
                            </ComboboxItem>
                          );
                        }}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </div>
                {selectedUser && selectedUser.memberships.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <Label>{t('demoMode.account')}</Label>
                    <Select
                      value={selectedAccountId ?? ''}
                      onValueChange={setSelectedAccountId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('demoMode.pickAccount')} />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedUser.memberships.map((m) => (
                          <SelectItem key={m.id} value={m.accountId}>
                            {(m.accountName ?? m.accountId) + ' - ' + m.accountType}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                <Button
                  type="button"
                  onClick={handleImpersonate}
                  disabled={
                    !selectedUser ||
                    !selectedAccountId ||
                    acting ||
                    loading
                  }
                >
                  {t('demoMode.impersonate')}
                </Button>
              </>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
